import html2canvas from "html2canvas"
import jsPDF from "jspdf"

const waitForImages = (element) => {
  const images = Array.from(element.querySelectorAll("img"))
  if (!images.length) return Promise.resolve()

  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve()
            return
          }
          const handleDone = () => resolve()
          img.addEventListener("load", handleDone, { once: true })
          img.addEventListener("error", handleDone, { once: true })
        })
    )
  )
}

const isSkippedInput = (el) => {
  const type = (el.getAttribute("type") || "").toLowerCase()
  return ["checkbox", "radio", "file", "hidden", "button", "submit", "reset"].includes(
    type
  )
}

/**
 * html2canvas often clips text inside <input>/<textarea>.
 * Replace them with sized <div>s in the cloned document so PDF text is fully visible.
 */
const prepareCloneForPdf = (clonedDoc) => {
  const style = clonedDoc.createElement("style")
  style.textContent = `
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    input, textarea, select {
      overflow: visible !important;
      line-height: 1.5 !important;
      height: auto !important;
      min-height: 40px !important;
      padding-top: 10px !important;
      padding-bottom: 10px !important;
      box-sizing: border-box !important;
    }
    textarea {
      white-space: pre-wrap !important;
      word-break: break-word !important;
    }
    table, tr, td, th, div, p, span, a, ul, li {
      overflow: visible !important;
    }
    .truncate {
      overflow: visible !important;
      text-overflow: unset !important;
      white-space: normal !important;
    }
    a.evidence-link, a[href] {
      display: inline-block !important;
      max-width: 100% !important;
      white-space: normal !important;
      word-break: break-all !important;
      line-height: 1.5 !important;
      padding: 4px 0 !important;
      color: #1d4ed8 !important;
      text-decoration: underline !important;
    }
    img {
      max-width: 100% !important;
      page-break-inside: avoid !important;
    }
  `
  clonedDoc.head.appendChild(style)

  // Expand truncated evidence link text fully in PDF
  clonedDoc.querySelectorAll("a.evidence-link, a[href]").forEach((anchor) => {
    anchor.classList.remove("truncate")
    anchor.style.overflow = "visible"
    anchor.style.whiteSpace = "normal"
    anchor.style.wordBreak = "break-all"
    anchor.style.lineHeight = "1.5"
    anchor.style.display = "inline-block"
    anchor.style.maxWidth = "100%"
  })

  const fields = clonedDoc.querySelectorAll("input, textarea")
  fields.forEach((el) => {
    if (el.tagName === "INPUT" && isSkippedInput(el)) return

    const replacement = clonedDoc.createElement("div")
    const value = el.value ?? ""
    replacement.textContent = value === "" ? " " : value

    const isTextarea = el.tagName === "TEXTAREA"
    const isReadonly = el.readOnly || el.disabled
    const minHeight = isTextarea
      ? `${Math.max(el.scrollHeight || 0, 100)}px`
      : "42px"

    replacement.setAttribute(
      "style",
      [
        "display:block",
        "width:100%",
        "box-sizing:border-box",
        "margin:0",
        "padding:10px 12px",
        "border:1px solid #d1d5db",
        "border-radius:4px",
        `background:${isReadonly ? "#f9fafb" : "#ffffff"}`,
        `color:${isReadonly ? "#374151" : "#111827"}`,
        "font-size:14px",
        "line-height:1.5",
        "font-family:inherit",
        "white-space:pre-wrap",
        "word-break:break-word",
        "overflow:visible",
        `min-height:${minHeight}`,
      ].join(";")
    )

    if (el.parentNode) {
      el.parentNode.replaceChild(replacement, el)
    }
  })
}

/**
 * Download a form element as a multi-page A4 PDF.
 * Preserves on-screen proportions (fits page width with margins; does not shrink oddly).
 */
export const downloadFormAsPdf = async (element, filename = "Form.pdf") => {
  if (!element) {
    throw new Error("Form element not found")
  }

  await waitForImages(element)

  const previousOverflow = element.style.overflow
  element.style.overflow = "visible"

  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: "#ffffff",
      scrollX: 0,
      scrollY: 0,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      onclone: (clonedDoc) => {
        prepareCloneForPdf(clonedDoc)
      },
    })

    const imgData = canvas.toDataURL("image/png")
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    })

    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = 10
    const contentWidth = pageWidth - margin * 2
    const contentPageHeight = pageHeight - margin * 2
    const imgHeight = (canvas.height * contentWidth) / canvas.width

    let heightLeft = imgHeight
    let position = margin

    pdf.addImage(imgData, "PNG", margin, position, contentWidth, imgHeight)
    heightLeft -= contentPageHeight

    while (heightLeft > 0) {
      position = margin - (imgHeight - heightLeft)
      pdf.addPage()
      // Fill page white first to avoid black strip artifacts between pages
      pdf.setFillColor(255, 255, 255)
      pdf.rect(0, 0, pageWidth, pageHeight, "F")
      pdf.addImage(imgData, "PNG", margin, position, contentWidth, imgHeight)
      heightLeft -= contentPageHeight
    }

    pdf.save(filename)
  } finally {
    element.style.overflow = previousOverflow
  }
}

export const buildFormPdfFilename = ({ title, adminName, year }) => {
  const safeTitle = (title || "Form").replace(/[^\w\-]+/g, "-")
  const safeName = (adminName || "Admin").replace(/[^\w\-]+/g, "-")
  const safeYear = year || new Date().getFullYear()
  return `${safeTitle}-${safeName}-${safeYear}.pdf`
}
